import { v5 } from 'uuid';
import {
    IItemSupport, IKeyedItem, IOptionSupport, ISessionSettings, ISettingsItem,
} from '../ts/Definitions';
import { EventType, ItemTypes } from '../ts/enums';
import Database from './database';
import { cloneObject, decrypt, valueMatches } from './utils';

// const keyToWatch = '602d9b80-5201-11ed-aa14-8b900502b7f9';

const setSystemTask = (c: ISettingsItem) => {
    c.isSystem = true;
    c.isCustom = false;
    c.isOwner = false;
    c.isOverride = false;
};
const setOwnerTask = (c: ISettingsItem) => {
    c.isSystem = false;
    c.isOwner = true;
    c.isOverride = false;
};
const setUserTask = (c: ISettingsItem) => {
    c.isOverride = true;
};

const settingsSections = [
    { section: 'creditSettings', items: 'credits', collectionType: ItemTypes.task },
    { section: 'penaltySettings', items: 'penalties', collectionType: ItemTypes.penalty },
    { section: 'positionSettings', items: 'positions', collectionType: ItemTypes.position },
    { section: 'permissionSettings', items: 'permissions', collectionType: ItemTypes.permission },
    { section: 'eventsSettings', items: 'events', collectionType: ItemTypes.event },
] as const;

const settingsSectionNames = new Set(settingsSections.map(({ section }) => section));

const structuredGeneralSettingKeys = [
    'userid',
    'adminLockPassword',
    'backgroundColor',
    'applicationTitle',
    'dailyRulesText',
    'dailyCost',
    'randomiseSettings',
    'showRemainingTimes',
    'showBank',
    'minReportTime',
    'maxReportTime',
    'reportWordCount',
    'dailyReporting',
    'dailyReportRequest',
    'morningReportRequest',
    'dailyReportingCredit',
    'weekendHandling',
    'randomVerifications',
    'verificationLimit',
    'penaltyPointConversionType',
    'version',
    'holidayMode',
    'holidayEndDate',
    'reportStyle',
    'disableDiscordProof',
    'disableDiscordShaming',
    'disableDiscordTicker',
    'hygieneBreaks',
    'hygieneIntervalMins',
    'hygienePillories',
    'hygienePenalties',
    'hygieneApplyCostPerMin',
    'hygieneCostPerMin',
    'hidePendingTasks',
    'customScheduledDays',
    'customScheduledDates',
    'siteEffectsSettings',
] as const;

// Stamped into settingsV2 by the backfill so a migrated record is never re-read from legacy.
export const STRUCTURED_VERSION_KEY = 'structuredVersion';
export const STRUCTURED_SETTINGS_VERSION = 1;

export const getStructuredSettingsSubscriptionKeys = (): string[] => [
    STRUCTURED_VERSION_KEY,
    ...structuredGeneralSettingKeys,
    ...settingsSections.map(({ section }) => section),
];

export const applyStructuredSubscriptionPatch = (
    snapshot: Record<string, unknown> | undefined,
    key: string,
    value: unknown
): Record<string, unknown> => {
    const updated = { ...(snapshot ?? {}) };
    if (value === undefined || value === null) delete updated[key];
    else updated[key] = value;
    return updated;
};

const toSettingsArray = <T extends ISettingsItem>(items: T[] | Record<string, T> | undefined): T[] => {
    if (!items) return [];
    if (Array.isArray(items)) return items.filter(Boolean);
    return Object.keys(items).flatMap((key) => {
        const item = (items as Record<string, T>)[key];
        if (!item || typeof item !== 'object') return [];
        return [{ ...item, key: item?.key ?? key } as T];
    });
};

const toSettingsMap = <T extends ISettingsItem>(items: T[] | Record<string, T> | undefined): Record<string, T> => {
    const array = toSettingsArray(items);
    return array.reduce((result, item) => {
        if (item?.key) result[item.key] = item;
        return result;
    }, {} as Record<string, T>);
};

export const buildStructuredSettingsSnapshot = (
    settings: ISessionSettings
): Record<string, unknown> => {
    const snapshot = cloneObject(settings) as Record<string, any>;
    if (!snapshot) return {};

    snapshot.customScheduledDays = cloneObject(snapshot.customScheduledDays ?? {});
    snapshot[STRUCTURED_VERSION_KEY] = STRUCTURED_SETTINGS_VERSION;

    settingsSections.forEach(({ section, items }) => {
        const sectionData = cloneObject(snapshot[section] ?? {}) as Record<string, unknown>;
        snapshot[section] = {
            ...sectionData,
            [items]: toSettingsMap((sectionData as any)[items]),
        };
    });

    return snapshot;
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
};

const appendStructuredDiff = (
    path: string,
    currentValue: unknown,
    previousValue: unknown,
    updates: Record<string, unknown>
) => {
    // Some booleans are treated as explicit toggles and should not be cleared to null
    // when a save payload omits them (for example, custom schedule saves).
    const preserveBooleanWhenOmitted =
        (path.endsWith('/dailyReporting') || path.endsWith('/permissionSettings/disablePermissions'))
        && previousValue !== undefined
        && previousValue !== null
        && typeof previousValue === 'boolean'
        && (currentValue === undefined || currentValue === null);
    if (preserveBooleanWhenOmitted) return;

    if (valueMatches(currentValue, previousValue)) return;

    const currentIsRecord = isRecord(currentValue);
    const previousIsRecord = isRecord(previousValue);

    if (!currentIsRecord || !previousIsRecord) {
        updates[path] = currentValue ?? null;
        return;
    }

    const keys = new Set([
        ...Object.keys(previousValue as Record<string, unknown>),
        ...Object.keys(currentValue as Record<string, unknown>),
    ]);

    keys.forEach((key) => {
        appendStructuredDiff(
            `${path}/${key}`,
            (currentValue as Record<string, unknown>)[key],
            (previousValue as Record<string, unknown>)[key],
            updates
        );
    });
};

export const buildStructuredSettingsUpdates = (
    userid: string,
    settings: ISessionSettings,
    previousSnapshot?: unknown
): Record<string, unknown> => {
    const basePath = `/settingsV2/${userid}`;
    const snapshot = buildStructuredSettingsSnapshot(settings);
    if (!hasStructuredSettingsData(previousSnapshot)) {
        return { [basePath]: snapshot };
    }

    const updates: Record<string, unknown> = {};
    appendStructuredDiff(basePath, snapshot, previousSnapshot, updates);
    if (Object.keys(updates).length === 0) {
        updates[basePath] = snapshot;
    }
    return updates;
};

const hasMeaningfulStructuredValue = (value: unknown): boolean => {
    if (value === undefined || value === null) return false;
    if (Array.isArray(value)) return value.some((entry) => entry !== undefined && entry !== null);
    if (typeof value !== 'object') return true;

    const record = value as Record<string, unknown>;
    if (Object.keys(record).length === 0) return false;
    return Object.values(record).some((entry) => hasMeaningfulStructuredValue(entry));
};

export const hasStructuredSettingsData = (snapshot: unknown): boolean => {
    if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) return false;
    return Object.values(snapshot as Record<string, unknown>).some((value) => hasMeaningfulStructuredValue(value));
};

/**
 * @param requireScheduleLinkage set false for template snapshots (system/owner profiles) where
 * customScheduledDays is meaningless, so their absence must not drag in the legacy blob.
 */
export const structuredSnapshotNeedsLegacyFallback = (
    snapshot: unknown,
    requireScheduleLinkage = true
): boolean => {
    if (!hasStructuredSettingsData(snapshot)) return true;
    if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) return true;

    const record = snapshot as Record<string, unknown>;
    // A stamped record is authoritative. Without this, an empty customScheduledDays (which
    // Firebase drops rather than storing as {}) forces a full legacy read on every load.
    const version = record[STRUCTURED_VERSION_KEY];
    if (typeof version === 'number' && version >= STRUCTURED_SETTINGS_VERSION) return false;

    if (
        requireScheduleLinkage &&
        (record.customScheduledDays === undefined || record.customScheduledDays === null)
    ) {
        return true;
    }

    return !settingsSections.some(({ section, items }) => {
        const sectionValue = record[section];
        if (!isRecord(sectionValue)) return false;
        const collection = (sectionValue as Record<string, unknown>)[items];
        if (Array.isArray(collection)) return collection.some((entry) => entry !== undefined && entry !== null);
        if (isRecord(collection)) return Object.keys(collection).length > 0;
        return false;
    });
};

export const shouldBackfillStructuredSettings = (
    structuredSnapshot: unknown,
    legacySnapshot: unknown,
    requireScheduleLinkage = true
): boolean => {
    if (hasStructuredSettingsData(structuredSnapshot) && !structuredSnapshotNeedsLegacyFallback(structuredSnapshot, requireScheduleLinkage)) return false;
    if (!legacySnapshot) return false;
    if (typeof legacySnapshot === 'string') return legacySnapshot.length > 0;
    if (typeof legacySnapshot === 'object' && !Array.isArray(legacySnapshot)) {
        return Object.keys(legacySnapshot as Record<string, unknown>).length > 0;
    }
    return false;
};

export const shouldRequestLegacySettingsFallback = (
    observedStructuredKeyCount: number,
    expectedStructuredKeyCount: number,
    structuredSnapshot: unknown,
    legacyFetchStarted: boolean,
    requireScheduleLinkage = true
): boolean => {
    if (legacyFetchStarted) return false;
    // Keys stream in one at a time. Judging a partial snapshot sees no populated sections
    // yet and wrongly pulls the legacy blob, so wait until every key has been delivered.
    if (observedStructuredKeyCount < expectedStructuredKeyCount) return false;
    if (!hasStructuredSettingsData(structuredSnapshot)) return true;
    return structuredSnapshotNeedsLegacyFallback(structuredSnapshot, requireScheduleLinkage);
};

export const shouldUseStructuredSettingsSnapshot = (
    observedStructuredKeyCount: number,
    expectedStructuredKeyCount: number,
    structuredSnapshot: unknown,
    hasLegacyFallback: boolean,
    legacyFallbackResolved = false,
    requireScheduleLinkage = true
): boolean => {
    if (!hasStructuredSettingsData(structuredSnapshot)) return false;
    if (!(hasLegacyFallback || observedStructuredKeyCount >= expectedStructuredKeyCount)) return false;
    if (structuredSnapshotNeedsLegacyFallback(structuredSnapshot, requireScheduleLinkage) && !hasLegacyFallback && !legacyFallbackResolved) return false;
    return true;
};

export const decodeSettingsSnapshot = (
    snapshot: unknown,
    fallback: ISessionSettings
): ISessionSettings => {
    const defaultSettings = cloneObject(fallback) as ISessionSettings;
    if (!snapshot) return defaultSettings;
    if (typeof snapshot === 'string') {
        return decrypt(snapshot, defaultSettings) as ISessionSettings;
    }
    if (typeof snapshot !== 'object' || Array.isArray(snapshot)) return defaultSettings;

    const input = cloneObject(snapshot as Record<string, unknown>) as Record<string, any>;
    const decoded = cloneObject(defaultSettings) as Record<string, any>;
    const useFallbackCollections = structuredSnapshotNeedsLegacyFallback(input);

    Object.keys(input).forEach((key) => {
        if (!settingsSectionNames.has(key as any)) decoded[key] = input[key];
    });

    settingsSections.forEach(({ section, items }) => {
        const sectionDefaults = cloneObject(decoded[section] ?? {}) as Record<string, unknown>;
        const sectionInput = input[section] as Record<string, unknown> | undefined;
        // If a structured section exists, only apply keys explicitly present in that section.
        // This avoids materializing fallback scalar defaults (for example disablePermissions=false)
        // into child snapshots where the field was intentionally absent.
        const hasSectionInput = !!sectionInput && typeof sectionInput === 'object' && !Array.isArray(sectionInput);
        decoded[section] = hasSectionInput ? { ...sectionInput } : { ...sectionDefaults };
        const sectionItemsInput = (sectionInput as any)?.[items];
        const resolvedItems = toSettingsArray(sectionItemsInput);
        decoded[section][items] = useFallbackCollections && resolvedItems.length === 0
            ? toSettingsArray(sectionDefaults[items] as ISettingsItem[] | Record<string, ISettingsItem> | undefined)
            : toSettingsArray(sectionItemsInput ?? sectionDefaults[items]);
    });

    return decoded as ISessionSettings;
};

const updateSettings = (settings: ISessionSettings, func: (item: ISettingsItem) => void) => {
    settingsSections.forEach(({ section, items }) => {
        (settings as any)[section]?.[items]?.forEach(func);
    });
}

const stripBaseSettingsFromChild = (settings: ISessionSettings, baseSettings: ISessionSettings,
    filterFunction: (items: ISettingsItem[], baseItems: ISettingsItem[]) => ISettingsItem[]) => {
    if (!baseSettings) return settings;
    if (!settings) return baseSettings;
    settingsSections.forEach(({ section, items }) => {
        const baseSection = (baseSettings as any)[section];
        if (!baseSection) return;

        if ((settings as any)[section] == null) {
            // Do not copy parent defaults into the stripped child payload.
            // Keep an empty section shell so downstream merge can still
            // inherit parent section-level fields safely.
            (settings as any)[section] = { [items]: [] };
            return;
        }

        const childSection = (settings as any)[section];
        const childItems = Array.isArray(childSection?.[items]) ? childSection[items] : [];
        const baseItems = Array.isArray(baseSection?.[items]) ? baseSection[items] : [];
        childSection[items] = filterFunction(childItems, baseItems);
    });
}

const itemDeletedOnParentOverridden = (i: ISettingsItem, parent: ISettingsItem) => parent.isDeleted // been deleted on the parent
    && !i.isDeleted // not deleted on the child and importantly the child record isn't the parent awaiting approval
    && i.requiresValidation === undefined
    && !i.hidden;

const itemRequiresApprovalToInclude = (i: ISettingsItem, parent: ISettingsItem) => !parent.requiresValidation // the parent has been approved
    && i.requiresValidation// and the child not quite updated yet
    && !parent.isDeleted;

const parentItemNowComplete = (i: ISettingsItem, parent: ISettingsItem) => !parent.requiresValidation && i.isNew === true && !i.hidden


/**
 * merges together the three levels allowed and outputs the merged result for processing
 * @param userSettings the user we are trying to enrich with system and owner settings
 * @param ownerSettings the owner if specified is first overlayed on the system settings
 * @param systemSettings the default system settings that act as base
 * @param isSystem is system asking for this, if so we return the lowest level without any merge
 * @returns the updated user settings with the objects in the 6 categories from the parent and the owner
 */
export const mergeSettings = (
    inputUserSettings: ISessionSettings | undefined,
    inputOwnerSettings: ISessionSettings | undefined,
    inputSystemSettings: ISessionSettings | undefined,
    isSystem: boolean,
): ISessionSettings => {
    // if the logged on user is the system, we don't need to do anything with the data
    if (isSystem) {
        return inputUserSettings as ISessionSettings;
    }

    if (!inputSystemSettings) return inputUserSettings as ISessionSettings;
    if (!inputUserSettings) return inputSystemSettings;
    const systemSettings = cloneObject(inputSystemSettings) as ISessionSettings;
    let ownerSettings = cloneObject(inputOwnerSettings) as ISessionSettings;
    let userSettings = cloneObject(inputUserSettings) as ISessionSettings;

    updateSettings(systemSettings, setSystemTask);
    if (userSettings) updateSettings(userSettings, setUserTask);
    if (ownerSettings) {
        updateSettings(ownerSettings, setOwnerTask);
        ownerSettings = mergeSettingsArray(ownerSettings, systemSettings, true);
        if (userSettings) userSettings = mergeSettingsArray(userSettings, ownerSettings, false);
    } else {
        userSettings = mergeSettingsArray(userSettings, systemSettings, false);
    }
    validateSettings(userSettings);
    return userSettings;
}

function validateSettings(userSettings: ISessionSettings): void {
    enforceSingleType(userSettings.eventsSettings.events, EventType.startOfDay);
    enforceSingleType(userSettings.permissionSettings.permissions, EventType.checkout);
}

function enforceSingleType(items: ISettingsItem[], eventType: EventType): void {
    const matches = items.filter(e => (e as any).eventType === eventType && !e.hidden && !e.isDeleted);
    if (matches.length > 1) {
        const hasOverride = matches.filter(e => e.isCustom);
        const hasOwner = matches.filter(e => e.isOwner);
        const hasSystem = matches.filter(e => e.isSystem);
        if (hasOverride.length > 0) {
            hasSystem.forEach(s => s.hidden = true);
            hasOwner.forEach(s => s.hidden = true);
            for (let i = 1; i < hasOverride.length; i += 1) hasOverride[i].isDeleted = true;
        } else if (hasOwner.length > 0) {
            hasSystem.forEach(s => s.hidden = true);
            for (let i = 1; i < hasOwner.length; i += 1) hasOwner[i].isDeleted = true;
        } else {
            for (let i = 1; i < hasSystem.length; i += 1) hasSystem[i].isDeleted = true;
        }
    }
}

/**
 * returns true if the items match on attributes that are not just processing flags
 * @param userItem the default item which exists higher in the hierarchy
 * @param baseItem the child item which would override the parent if different
 * @returns are the items the same
 */
const areEqual = (userItem: ISettingsItem, baseItem: ISettingsItem | undefined): boolean => {
    // if (userItem.key === keyToWatch) {
    //     userItem.key.toString();
    // }
    if (!userItem || !baseItem) return false;
    if (Boolean(userItem.isDeleted) !== Boolean(baseItem.isDeleted)) return false;
    const { isCustom, isNew } = userItem;
    const matches = getDifferences(userItem, baseItem).length === 0;

    if (isCustom && matches && !isNew) {
        userItem.isNew = false;
        userItem.isCustom = true;
        return false;
    }
    return matches;
}

const EXCLUDED_DIFF_KEYS = new Set(['isDeleted', 'isOverride', 'isCustom', 'isNew', 'img', 'isSystem', 'isOwner', 'requiresValidation', 'createdDate', 'createdBy', 'key', 'itemType']);

export const getDifferences = (userItem: ISettingsItem, baseItem: ISettingsItem | undefined): string[] => {
    // if (userItem.key === keyToWatch) {
    //     userItem.key.toString();
    // }
    if (!userItem || !baseItem) return Object.keys(userItem ?? baseItem);
    Object.keys(userItem).forEach((key) => {
        if (key === 'key' || key === 'isDeleted') return;
        const userVal = (userItem as any)[key];
        const baseVal = (baseItem as any)[key];
        const matches = valueMatches(userVal, baseVal);
        if (matches) delete (userItem as any)[key];
    })
    if (!baseItem.hidden && !userItem.hidden) delete userItem.hidden;
    const optionItem = userItem as IOptionSupport;
    const optionBaseItem = baseItem as IOptionSupport;
    if (!optionItem.options?.length && !optionBaseItem.options?.length)
        delete optionItem.options;

    const itemsItem = userItem as IItemSupport;
    const itemsBaseItem = baseItem as IItemSupport;
    if (!itemsItem.itemsUsed?.length && !itemsBaseItem.itemsUsed?.length)
        delete itemsItem.itemsUsed;

    return Object.keys(userItem).filter(key => !EXCLUDED_DIFF_KEYS.has(key));
}

const userCreatedTask = (i: ISettingsItem, userid: string) => {
    if (!i.createdBy) return false;
    return userid && userid.startsWith(i.createdBy);
}

const CREATOR_HYDRATION_EXCLUDED_KEYS = new Set([
    'isDeleted', 'isOverride', 'isCustom', 'isNew', 'img', 'isSystem', 'isOwner', 'requiresValidation'
]);

const hasMeaningfulValue = (value: unknown): boolean => {
    if (value === undefined || value === null || value === '') return false;
    if (Array.isArray(value)) return value.length > 0;
    return true;
};

export const hydrateItemFromCreator = <T extends ISettingsItem>(item: T, creatorItem?: ISettingsItem): T => {
    if (!item) return item;
    if (!creatorItem) return cloneObject(item) as T;

    const hydrated = cloneObject(item) as Record<string, unknown>;
    const creatorRecord = creatorItem as unknown as Record<string, unknown>;
    Object.keys(creatorItem).forEach((key) => {
        if (CREATOR_HYDRATION_EXCLUDED_KEYS.has(key)) return;
        const sourceValue = creatorRecord[key];
        const targetValue = hydrated[key];
        if (!hasMeaningfulValue(sourceValue)) return;
        if (hasMeaningfulValue(targetValue)) return;
        hydrated[key] = typeof sourceValue === 'object' ? cloneObject(sourceValue) : sourceValue;
    });
    return hydrated as T;
};

export const isCreatorItemHandled = (creatorItem?: ISettingsItem): boolean => {
    if (!creatorItem) return false;
    return creatorItem.requiresValidation !== true;
};
/**
 * takes the two settings and appends new items from each to those that are shared in both, with the merge of the shared items 
 * @param child the child item which will be enriched
 * @param base the parent which is used to enrich the child
 * @returns the updated child with the new items in the array
 */
const mergeSettingsArray = (child: ISessionSettings,
    base: ISessionSettings, isOwner: boolean): ISessionSettings => {
    if (!base) return child;
    const mergeSettingsItems = (items: ISettingsItem[], defaultItems: ISettingsItem[], collectionType: ItemTypes) => {
        items.forEach((i, idx) => {
            // if (i.key === keyToWatch) {
            //     i.key.toString();
            // }
            const defaultMatch = defaultItems.find(df => df.key === i.key);
            if (!defaultMatch) {
                i.itemType = collectionType;
                i.isCustom = true;
            } else {
                const overlaidCopy = { ...defaultMatch, ...i };
                if (!Object.prototype.hasOwnProperty.call(i, 'disablePermissions')) {
                    if (defaultMatch.disablePermissions === true) {
                        overlaidCopy.disablePermissions = true;
                    } else {
                        delete (overlaidCopy as any).disablePermissions;
                    }
                }
                // if (defaultMatch.hidden
                //     && !i.hidden
                //     && i.isOverride
                //     && !overlaidCopy.isDeleted) {
                // console.debug(`${overlaidCopy.name} set to visible as child has overridden settings`);
                // overlaidCopy.hidden = false;
                // }
                overlaidCopy.itemType = collectionType;
                if ((child.userid && userCreatedTask(overlaidCopy, child.userid))) { /*itemDerivedFromUser(i, defaultMatch) ||*/
                    // console.debug(`${overlaidCopy.name} set to custom task as child has overridden settings`);
                    overlaidCopy.isNew = false;
                    overlaidCopy.requiresValidation = false;
                    overlaidCopy.isCustom = !isOwner;
                    overlaidCopy.isOwner = isOwner;
                    overlaidCopy.isSystem = false;
                    overlaidCopy.isDeleted = i.isDeleted;

                }
                if (itemDeletedOnParentOverridden(i, defaultMatch)) {
                    overlaidCopy.isDeleted = false;
                    // console.debug(`${overlaidCopy.name} set to not deleted as parent had deleted item child had overridden`);
                }
                if (itemRequiresApprovalToInclude(i, defaultMatch)) {// and the parent record hasn't been deleted
                    // console.debug(`${overlaidCopy.name} set to requires approval for child`);
                    overlaidCopy.requiresValidation = false;
                    overlaidCopy.isNew = true;
                    overlaidCopy.hidden = defaultMatch.hidden; // set the child to be same hidden as parent as we are still awaiting a decision
                }

                if (parentItemNowComplete(i, defaultMatch)) {
                    // console.debug(`${overlaidCopy.name} set to active to child as parent approved`);
                    overlaidCopy.isNew = false;
                }
                overlaidCopy.isOverride = !isOwner;
                if (i.isCustom && !isOwner) overlaidCopy.isOwner = false;
                if (i.isCustom) overlaidCopy.isSystem = false;
                if (i.isOwner) overlaidCopy.isSystem = false;
                items[idx] = overlaidCopy;
            }
        });
        defaultItems.forEach((item) => {
            const idx = items.findIndex(i => i.key === item.key);
            // if (item.key === keyToWatch) {
            //     item.key.toString();
            // }
            if (idx === -1) {
                const copyItem = { ...item };
                copyItem.itemType = collectionType;
                const inheritedApprovedItem = copyItem.isNew === true
                    && copyItem.requiresValidation !== true
                    && !!copyItem.createdBy;
                if (inheritedApprovedItem) copyItem.isNew = false;
                items.push(copyItem);
            }
        });
    }

    settingsSections.forEach(({ section, items, collectionType }) => {
        let childSection = (child as any)[section];
        const baseSection = (base as any)[section];
        if (!baseSection) return;

        if (!childSection) {
            childSection = { [items]: [] };
        }

        // Inherit section-level fields (e.g. disablePermissions) from the parent
        // while preserving any explicit child override values.
        const mergedSection = {
            ...baseSection,
            ...childSection,
        };
        if (section === 'permissionSettings' && !Object.prototype.hasOwnProperty.call(childSection, 'disablePermissions')) {
            if (mergedSection.disablePermissions !== true) {
                delete (mergedSection as any).disablePermissions;
            }
        }
        const childItems = Array.isArray(mergedSection[items]) ? mergedSection[items] : [];
        mergedSection[items] = childItems;
        (child as any)[section] = mergedSection;

        const baseItems = Array.isArray(baseSection[items]) ? baseSection[items] : [];
        mergeSettingsItems((child as any)[section][items], baseItems, collectionType);
    });

    return child;
}

/**
 * new simpiler settings strip which takes out anything in the base object which matches current object
 * @param userSettings the user or the owner profile if the owner is editing their own profile
 * @param ownerSettings the owner of the user, empty if dealing with unowned or editing owner profiles
 * @param systemSettings the system settings which act as base
 * @param isSystem is this being called by the system, if so, nothing is stripped
 * @returns 
 */
export const stripSettings = (
    userSettings: ISessionSettings,
    ownerSettings: ISessionSettings | undefined,
    systemSettings: ISessionSettings | undefined,
    isSystem: boolean, userid: string, database: Database): ISessionSettings => {
    if (!userSettings || isSystem) return userSettings;
    const mergedSettings = mergeSettings(ownerSettings, undefined, systemSettings, isSystem);
    const tempSettings = cloneObject(userSettings) as ISessionSettings;
    const filterItems = (items: ISettingsItem[], baseItems: ISettingsItem[]): ISettingsItem[] => {
        if (!items) return [];
        return items.filter(i => !areEqual(i, baseItems.find(bi => bi.key === i.key)));
    };
    stripBaseSettingsFromChild(tempSettings, mergedSettings, filterItems);
    stripImagesFromSettings(tempSettings, userid, database);
    return tempSettings;
}
/**
 * Takes the settings and resets all the is new flags currently pending a user decision
 * in the case of a new user, whom would take all new items as system default.
 * @param settings the settings to update
 */
export const setNewItemsToHandled = (settings: ISessionSettings): void => {
    updateSettings(settings, (i) => { if (i.isNew) i.isNew = false });
}


export const stripImagesFromSettings = (settings: ISessionSettings, userid: string, database: Database) => {
    const updates: { imgId: string, img: string }[] = [];
    let updated = false;
    const stripImages = (items: ISettingsItem[] | Record<string, ISettingsItem> | null | undefined) => {
        const arrayItems = Array.isArray(items)
            ? items.filter(Boolean)
            : items && typeof items === 'object'
                ? Object.values(items).filter(Boolean)
                : [];

        if (arrayItems.length > 0) {
            arrayItems.forEach(item => {
                if (!item) return;
                if (item.img) {
                    const img = item.img;
                    if (!item.imgId) {
                        const imgId = v5(item.img, v5(userid, item.key));
                        item.imgId = imgId;
                        updated = true;
                        updates.push({ imgId, img });
                    }
                    delete item.img;
                }
                const optionItem = (item as IOptionSupport & IKeyedItem);
                const optionImages = Array.isArray(optionItem.optionImages)
                    ? optionItem.optionImages.filter(Boolean)
                    : [];
                optionImages.forEach((option) => {
                    if (!option) return;
                    if (option.img) {
                        if (!option.imgId) {
                            const imgId = v5(option.img, v5(userid, optionItem.key));
                            option.imgId = imgId;
                            updated = true;
                            updates.push({ imgId, img: option.img });
                        }
                        delete option.img;
                    }
                });
            });
        }
    }

    settingsSections.forEach(({ section, items }) => {
        const sectionData = (settings as any)[section];
        if (sectionData) stripImages(sectionData[items]);
    });

    if (updated) {
        void database.updateBatchImageDb(updates);
    }
}

